import React, { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { LEGAL_LINKS } from '../constants/legalLinks';

// Cookie / local-storage consent (UK PECR + GDPR). Essential storage (Firebase
// Auth session, Firestore offline cache) is exempt from consent; everything
// else — currently only the BugHerd feedback sidebar's third-party script — is
// "functional" and must not load until the user opts in. The decision is kept
// in localStorage (a consent record is itself an essential purpose) and
// broadcast via a DOM event so gated loaders (BugHerdScript.js) can react
// without a page reload.

const STORAGE_KEY = 'eddp-cookie-consent-v1';
export const CONSENT_CHANGED_EVENT = 'eddp-cookie-consent-changed';

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

export function hasFunctionalConsent() {
  const consent = getCookieConsent();
  return consent?.functional === true;
}

function saveConsent(functional) {
  const record = {
    essential: true,
    functional,
    decidedAt: new Date().toISOString(),
    version: 1,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (err) {
    console.error('CookieConsent: failed to persist decision', err);
  }
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: record }));
  } catch (_) {}
  return record;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setVisible(true);
  }, []);

  if (!visible) return null;

  const decide = (functional) => {
    saveConsent(functional);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-[9999] p-4 sm:p-6"
    >
      <div className="mx-auto max-w-3xl bg-white rounded-xl border border-slate-100 shadow-slick p-6">
        <h2 className="text-base font-bold text-eddp-dark mb-1">Cookies and similar technologies</h2>
        <p className="text-sm text-eddp-muted mb-4">
          We use essential storage to keep you signed in and the app working — that
          never needs consent. With your permission we also load a functional
          third-party feedback tool. See our{' '}
          <a
            href={LEGAL_LINKS.cookies}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-eddp-primary hover:text-eddp-dark"
          >
            cookie policy
          </a>{' '}
          for details.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button variant="secondary" onClick={() => decide(false)}>
            Essential only
          </Button>
          <Button variant="primary" onClick={() => decide(true)}>
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
